<template>
  <div>
    <b-modal
      id="modal-add-orthophoto"
      :title="$t('master.orthophoto.upload.title')"
      centered
      hide-footer
      size="lg"
      @hidden="resetState"
    >
      <div>
        <div class="upload-drop-zone" @dragover.prevent @drop.prevent="handleDrop">
          <div class="upload-icon mb-3">
            <i class="icon-outline_upload"></i>
          </div>
          <div class="text-muted mb-2">
            {{ $t('master.orthophoto.upload.dropFile') }}
          </div>
          <div class="text-muted mb-2">
            {{ $t('master.orthophoto.upload.format') }}
          </div>
          <div class="text-muted mb-4 font-size-12">
            {{ $t('master.orthophoto.upload.crsHint') }}
          </div>

          <input
            ref="fileInput"
            type="file"
            class="d-none"
            accept=".tif"
            @change="handleFileSelect"
          />
          <McButton
            icon="icon-outline_folder"
            variant="primary"
            :text="$t('master.orthophoto.upload.chooseFolder')"
            size="sm"
            @click="chooseLocalFile"
          />
        </div>

        <div class="mt-4">
          <div class="text-center mb-3 text-muted">
            {{ $t('master.orthophoto.upload.or') }}
          </div>

          <div class="google-drive-section">
            <div class="d-flex align-items-center">
              <div class="google-drive-icon">
                <img src="@/assets/images/drive.png" alt="Google Drive Icon">
                <span class="ms-2">{{ $t('master.orthophoto.upload.googleDriveLink') }}</span>
              </div>
              <div class="flex-grow-1">
                <input
                  v-model="googleDriveLink"
                  type="text"
                  :placeholder="$t('master.orthophoto.upload.googleDrivePlaceholder')"
                  class="form-control"
                />
              </div>
              <McButton
                variant="outline-primary"
                :text="$t('master.orthophoto.upload.apply')"
                @click="applyGoogleDriveLink" class="ms-2"
              />
            </div>
            <span v-if="googleDriveErrorMessage" class="google-drive-error-message red-500">{{
              googleDriveErrorMessage
            }}</span>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import McButton from '@/components/McButton'
import { CREATE_ORTHOPHOTO } from '@/services/api/master/orthophoto'
import { supportsPersistentFileHandle } from '@/services/upload/file-handle-store'
import { isUploadPageUnloading } from '@/services/upload/page-lifecycle'
import { getUploadHandler } from '@/services/upload/registry'

const createUserError = (message) => Object.assign(new Error(message), { isUserError: true })

const GOOGLE_DRIVE_ERROR_KEYS = {
  'E-O1-C1/5': 'master.orthophoto.upload.messages.googleDriveLinkNotAccessible',
  'E-O1-C1/3': 'master.orthophoto.upload.messages.googleDriveUnsupportedFormat',
}

const GOOGLE_DRIVE_INVALID_URL_MESSAGE = 'Google Drive URL must use https://drive.google.com'
const UPLOAD_INTERRUPTED_KEY = 'global.fileUploader.messages.uploadInterrupted'

export default {
  name: 'ModalAddOrthophoto',
  components: {
    McButton,
  },
  data() {
    return {
      file: null,
      googleDriveLink: '',
      googleDriveErrorMessage: '',
    }
  },
  methods: {
    async handleDrop(e) {
      const files = e.dataTransfer.files
      if (!files.length) return

      let fileHandle = null
      const item = e.dataTransfer.items?.[0]
      if (item?.getAsFileSystemHandle) {
        fileHandle = await item.getAsFileSystemHandle().catch(() => null)
      }
      this.processFile(files[0], fileHandle?.kind === 'file' ? fileHandle : null)
    },
    handleFileSelect(e) {
      const files = e.target.files
      if (files.length) this.processFile(files[0])
    },
    async chooseLocalFile() {
      if (!supportsPersistentFileHandle()) {
        this.$refs.fileInput.click()
        return
      }

      try {
        const [fileHandle] = await window.showOpenFilePicker({
          multiple: false,
          excludeAcceptAllOption: true,
          types: [
            {
              description: 'TIFF',
              accept: { 'image/tiff': ['.tif'] },
            },
          ],
        })
        await this.processFile(await fileHandle.getFile(), fileHandle)
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.$mcToast.error(this.$t('master.orthophoto.upload.messages.openFileFailed'))
        }
      }
    },
    async processFile(file, fileHandle = null) {
      if (!file.name.toLowerCase().endsWith('.tif')) {
        this.$mcToast.error(this.$t('master.orthophoto.upload.messages.invalidFormat'))
        return
      }

      this.file = file

      this.$bvModal.hide('modal-add-orthophoto')
      const handler = getUploadHandler('orthophoto')

      await this.$store.dispatch('upload/startUpload', {
        file,
        options: {
          uploadType: 'orthophoto',
          resumeMode: fileHandle ? 'file-handle' : 'file-picker',
          customUploadHandler: async ({
            file,
            updateMetadata,
            complete,
            fail,
            uploadId,
          }) => {
            try {
              await handler.start({
                uploadId,
                file,
                fileHandle,
                apollo: this.$apollo,
                update: updateMetadata,
                complete,
                onCreated: (payload) => this.$emit('created', payload),
              })
              this.$emit('success')
              this.$mcToast.success(
                this.$t('master.orthophoto.upload.messages.uploadSuccess')
              )
            } catch (error) {
              if (error.name === 'AbortError' || isUploadPageUnloading()) return

              const isRecoverableInterruption =
                error.isResumable && !error.isFatalUploadError && !error.isUserError
              if (isRecoverableInterruption) {
                updateMetadata({
                  status: 'waiting_resume',
                  error: null,
                  errorKey: null,
                  interruptionReason: 'transient',
                  autoResumeAttempts: 1,
                  nextAutoResumeAt: Date.now() + 3000,
                })
                return
              }

              const errorMessage = error.isUserError
                ? error.message
                : this.$t(UPLOAD_INTERRUPTED_KEY)
              const errorMetadata = {
                error: error.isUserError ? errorMessage : null,
                errorKey: error.isUserError ? null : UPLOAD_INTERRUPTED_KEY,
              }

              if (error.isResumable) {
                updateMetadata({
                  ...errorMetadata,
                  status: 'paused',
                  interruptionReason: 'runtime-error',
                })
              } else {
                fail(errorMetadata.error, errorMetadata.errorKey)
              }
              this.$mcToast.error(errorMessage)
            }
          },
        },
      })
    },
    async applyGoogleDriveLink() {
      this.googleDriveErrorMessage = ''
      if (!this.googleDriveLink.trim()) {
        this.googleDriveErrorMessage = this.$t(
          'master.orthophoto.upload.messages.googleDriveRequired'
        )
        return
      }
      try {
        const input = {
          label: `Orthophoto ${new Date().toLocaleString()}`,
          sourcePlatform: 'GoogleDrive',
          sourceType: 'TIFF',
          sourceUrl: this.googleDriveLink.trim(),
        }

        const res = await this.$apollo.mutate({
          mutation: CREATE_ORTHOPHOTO,
          variables: { input },
        })

        const result = res.data.createOrthophoto
        if (result.isSuccessful) {
          this.$emit('success')
          this.$mcToast.success(this.$t('master.orthophoto.upload.messages.uploadSuccess'))
          this.$bvModal.hide('modal-add-orthophoto')
        } else {
          const errorKey =
            GOOGLE_DRIVE_ERROR_KEYS[result.errorCode] ||
            (result.message?.includes(GOOGLE_DRIVE_INVALID_URL_MESSAGE)
              ? 'master.orthophoto.upload.messages.googleDriveInvalidLink'
              : 'master.orthophoto.upload.messages.uploadFailed')
          throw createUserError(this.$t(errorKey))
        }
      } catch (error) {
        this.googleDriveErrorMessage = error.isUserError
          ? error.message
          : this.$t('global.fileUploader.messages.uploadInterrupted')
      }
    },
    resetState() {
      this.file = null
      this.googleDriveLink = ''
      this.googleDriveErrorMessage = ''
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },
    show() {
      this.$bvModal.show('modal-add-orthophoto')
    },
  },
}
</script>

<style lang="scss" scoped>
.upload-drop-zone {
  border: 2px dashed $teal-100;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  background-color: $white;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: $primary;
  }

  .upload-icon {
    font-size: 24px;
    color: $primary;
  }
}

.google-drive-section {
  .google-drive-icon {
    padding:8px 15px 7px;
    background-color: $gray-200;
    font-weight: 500;
    border-radius: 5px 0 0 5px;
    border: 1px solid $teal-200;
    border-right: 0;
  }

  .form-control {
    border-radius:0 4px 4px 0;
  }
}
</style>
